import { useContext, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../../../components/MyButton";
import MyTextArea from "../../../../components/inputs/MyTextArea";
import { useBoardItemComment } from "../../../../hooks/useBoardItemComment";
import { SiteContext } from "../../../../context/SiteProvider";
import MyInputCaptcha from "../../../../components/inputs/MyInputCaptcha";
import { AuthContext } from "../../../../context/AuthProvider";
import MyCheckboxCircle from "../../../../components/inputs/MyCheckboxCircle";
import MyInputFile from "../../../../components/inputs/MyInputFile";
import { doUploadFiles } from "../../../../utils/file";
import { useFile } from "../../../../hooks/useFile";
import { useBoardItem } from "../../../../hooks/useBoardItem";

const ResearchCommentForm03 = ({
  board,
  boardItem,
  selectComment,
  setSelectComment,
  setReplyMode,
}) => {
  const { createBoardItemCommentMutation, updateBoardItemCommentMutation } =
    useBoardItemComment();
  const { updateBoardItemMutation } = useBoardItem();
  const queryClient = useQueryClient();
  const { setLoadingScreen } = useContext(SiteContext);
  const { profile, isAuthenticated } = useContext(AuthContext);
  const { getFileUploadUrl } = useFile();

  const initValues = {
    comment: "",
    files: [],
    captchaToken: "",
    isPrivate: false,
    isRequiredCaptcha: false,
  };

  const validationSchema = yup.object({
    files: yup
      .array()
      .of(yup.mixed().required("파일을 선택해 주세요."))
      .max(5, "최대 5개의 파일만 업로드할 수 있습니다.")
      .nullable(),
    captcha: yup.string().when("isRequiredCaptcha", {
      is: true,
      then: () =>
        yup.string().required("자동입력방지 인증코드를 입력해주세요."),
    }),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const showCaptcha = useMemo(
    () => !!board?.options?.useCaptchaInComment,
    [board]
  );

  const onCancel = () => {
    methods.reset();

    if (!selectComment) setReplyMode(false);
    setSelectComment(null);
  };

  const onSubmit = async (vals) => {
    if (board.boardCode) {
      const payload = {
        comment: vals.comment,
        isPrivate: board.options?.usePrivateInComment ? vals.isPrivate : false,
      };

      if (showCaptcha) {
        if (!vals.captchaToken) {
          methods.setError("captcha", {
            message: "인증을 진행해주세요.",
          });
          return;
        }
        payload.captchaToken = vals.captchaToken;
      }

      if (!selectComment?.commentNo) {
        payload.userId = profile.userId;
      } else {
        payload.userId = selectComment?.writer?.userId;
      }

      setLoadingScreen(true);

      let uploadedFiles = [];
      if (vals.files?.length) {
        uploadedFiles = await doUploadFiles(vals.files, getFileUploadUrl, {
          boardCode: board?.boardCode,
          isComment: true,
        });
      }

      payload.attachedFiles = uploadedFiles.map((file) => ({
        fileName: file.name,
        fileId: file.fileId,
      }));

      if (!selectComment?.commentNo) {
        createBoardItemCommentMutation.mutate(
          {
            boardCode: board?.boardCode,
            itemNo: boardItem.itemNo,
            payload,
          },
          {
            onSuccess: (res) => {
              if (res) {
                updateBoardItemMutation.mutate(
                  {
                    boardCode: board?.boardCode,
                    itemNo: boardItem?.itemNo,
                    payload: {
                      title: boardItem.title,
                      body: boardItem.body,
                      additionalItems: {
                        status:
                          boardItem?.additionalItems?.status === "waiting"
                            ? "reviewing"
                            : boardItem?.additionalItems?.status,
                      },
                      attachedFiles: boardItem.attachedFiles,
                      userId: boardItem.writer.userId,
                    },
                  },
                  {
                    onSuccess: (res) => {
                      if (res) {
                        alert("답변이 등록되었습니다.");
                        queryClient.invalidateQueries({
                          queryKey: ["/board-item/{boardCode}/{itemNo}"],
                        });
                        queryClient.invalidateQueries({
                          queryKey: [
                            "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                          ],
                        });
                        methods.reset();
                        setSelectComment(null);
                        setReplyMode(false);
                      }
                    },
                    onSettled: () => {
                      setLoadingScreen(false);
                      if (showCaptcha) {
                        queryClient.invalidateQueries({
                          queryKey: ["/site-common/captcha/generate"],
                        });
                      }
                    },
                  }
                );
              } else {
                alert("댓글 업로드 중 오류가 발생했습니다.");
              }
            },
          }
        );
      } else {
        updateBoardItemCommentMutation.mutate(
          {
            boardCode: board?.boardCode,
            commentNo: selectComment.commentNo,
            itemNo: boardItem?.itemNo,
            payload,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("답변이 등록되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item/{boardCode}/{itemNo}"],
                });
                queryClient.invalidateQueries({
                  queryKey: [
                    "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                  ],
                });
                methods.reset();
                setSelectComment(null);
                setReplyMode(false);
              } else {
                alert("댓글 업로드 중 오류가 발생했습니다.");
              }
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      }
    } else {
      alert("게시글 카테고리가 존재하지 않습니다.");
    }
  };

  useEffect(() => {
    if (selectComment?.commentNo) {
      if (selectComment.comment) {
        methods.setValue("comment", selectComment.comment);
      }
      methods.setValue("isPrivate", selectComment.isPrivate);

      if (selectComment.attachedFiles?.length) {
        const newFiles = selectComment.attachedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file: {
            ...file,
            type: file.contentType,
          },
        }));
        methods.setValue("files", newFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectComment]);

  useEffect(() => {
    methods.setValue("isRequiredCaptcha", showCaptcha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaptcha]);

  return (
    <div>
      <h2 className="font-semibold lg:text-3xl md:text-2xl text-xl mb-4">
        {!selectComment?.commentNo ? "답변 작성" : "답변 수정"}
      </h2>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="comment"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              답변내용
            </label>
            <div className="flex-1">
              <MyTextArea id="comment" name="comment" rows="8" cols="30" />
            </div>
          </div>

          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="files"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              파일 첨부
            </label>
            <div className="flex-1">
              <MyInputFile
                name="files"
                multiple
                placeholder="파일을 첨부해 주세요."
                hint="*파일 첨부는 최대 5개까지 업로드 가능합니다."
              />
            </div>
          </div>

          {board.options?.usePrivateInComment && (
            <div className="flex flex-wrap md:mb-5 mb-3">
              <label htmlFor="isPrivate" className="w-[150px] font-semibold">
                비밀 글
              </label>
              <div className="flex-1">
                <MyCheckboxCircle
                  name="isPrivate"
                  label="*체크 시 ,관리자, 게시글 작성자, 답변 작성자만 확인 할 수 있습니다."
                  labelClassName="font-medium"
                />
              </div>
            </div>
          )}

          {showCaptcha && (
            <MyInputCaptcha
              name="captchaToken"
              labelClass="md:w-[150px]"
              className={isAuthenticated ? "hidden" : ""}
            />
          )}

          <div className="flex items-center sm:gap-4 gap-2 md:mt-10 mt-5">
            <MyButton
              variant="outlined"
              className="font-semibold ml-auto min-w-[120px]"
              onClick={onCancel}
            >
              <span className="text-gray-dark">취소</span>
            </MyButton>

            <MyButton
              variant="contained"
              color="primary"
              className="font-semibold min-w-[120px]"
              type="submit"
            >
              등록
            </MyButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ResearchCommentForm03;
